import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { DATE_FORMAT } from '../../../../CommonComponents/OPL/dates/utils';
import { Modal } from '../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../CommonComponents/OPL/Modal/ModalHeader';
import { Tab, TabList, TabPanel, Tabs } from '../../../../CommonComponents/OPL/Tab';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { useSaveAccountMutation } from '../../api/useSaveAccountMutation';
import { useStockOptionsContext } from '../../context/useStockOptionsContext';
import { GrantToDelete, GrantToUpsert } from '../../StockOptions.meta';
import { DeleteStockGrantModal } from '../DeleteStockGrantModal/DeleteStockGrantModal';
import { Summary } from './components/Summary/Summary';
import { EXCLUDE_OPTION_VALUE } from './components/Summary/Summary.meta';
import { VestingSchedule } from './components/VestingSchedule/VestingSchedule';
import { Account, FormValues, Grant, validation } from './UpsertGrantModal.meta';

interface Props {
  clientId: string;
  grantToUpsert: GrantToUpsert;
  onClose(): void;
}

export const UpsertGrantModal = ({ clientId, grantToUpsert, onClose }: Props) => {
  const [grantToDelete, setGrantToDelete] = useState<GrantToDelete | null>(null);

  const { grant, account, isAddingNewGrant } = grantToUpsert;

  const { options } = useStockOptionsContext();

  const getDefaultCustomAssetBucketIndex = () => {
    if (options.AssetBucketingByManagementStyle) {
      const selectedManagementStyle = options.ManagementStyles.find(
        option => option.id === account.OrionManagementStyleId,
      );

      return selectedManagementStyle?.defaultBucket ?? EXCLUDE_OPTION_VALUE;
    }

    if (account.CustomAssetBucketIndex === null) {
      return options.DefaultAssetBucketIndex;
    }

    return account.CustomAssetBucketIndex;
  };

  const formBag = useForm<FormValues>({
    defaultValues: {
      name: account.AccountName || '',
      owner: account.AccountOwner || '',
      grantType: grant.GrantType,
      marketValue: grant.Value,
      totalShares: grant.TotalShares,
      customAssetBucketIndex: getDefaultCustomAssetBucketIndex(),
      grantDate: grant.GrantDate,
      expectedReturn: grant.ExpectedReturn,
      price: grant.Price,
      assetClass: account.StockOptionAssetClass,
      earmarks: (account.AccountEarmark || []).map(({ Tooltip, Type }) => ({
        label: Tooltip,
        value: Type,
      })),

      vestingList:
        grant.VestingList && grant.VestingList.length > 0
          ? grant.VestingList.map(item => ({
              ...item,
              VestingDate: item.VestingDate
                ? moment.utc(item.VestingDate).format(DATE_FORMAT)
                : null,
              DispositionDate: item.DispositionDate
                ? moment.utc(item.DispositionDate).format(DATE_FORMAT)
                : null,
              ExerciseDate: item.ExerciseDate
                ? moment.utc(item.ExerciseDate).format(DATE_FORMAT)
                : null,
              ExpirationDate: item.ExpirationDate
                ? moment.utc(item.ExpirationDate).format(DATE_FORMAT)
                : null,
              GrantDate: item?.GrantDate ? moment.utc(item.GrantDate).format(DATE_FORMAT) : null,
            }))
          : [
              {
                VestingDate: null,
                DispositionStrategy: null,
                DispositionDate: null,
                DispositionYear: null,
                ExerciseStrategy: null,
                ExerciseDate: null,
                ExerciseYear: null,
              },
            ],

      ownedShares: grant.OwnedShares ?? null,
      ownedExerciseDate: grant.OwnedExerciseDate
        ? moment.utc(grant.OwnedExerciseDate).format(DATE_FORMAT)
        : null,
      ownedStrikePrice: grant.OwnedStrikePrice ?? null,
      ownedFmvAtExercise: grant.OwnedFmvAtExercise ?? null,
      ownedDispositionStrategy: grant.OwnedDispositionStrategy ?? null,
      ownedDispositionDate: grant.OwnedDispositionDate
        ? moment.utc(grant.OwnedDispositionDate).format(DATE_FORMAT)
        : null,
      ownedDispositionYear:
        grant.OwnedDispositionYear !== null && grant.OwnedDispositionYear !== undefined
          ? String(grant.OwnedDispositionYear)
          : null,

      orionManagementStyleId: account.OrionManagementStyleId ?? null,
      ownerModel: account.OwnerModel ?? [{ Id: null, Name: '', OwnerType: 0, Percentage: 1 }],
      product: account.StockOptionProductDetails
        ? {
            id: account.StockOptionProductDetails.Id,
            ticker: account.StockOptionProductDetails.Ticker,
            price: account.StockOptionProductDetails.Price,
          }
        : null,
    },
    context: {
      OptionsPerGrantType: options.OptionsPerGrantType,
    },
    resolver: yupResolver(validation),
  });

  const {
    mutateAsync: saveAccount,
    isLoading: isSavingAccount,
    error: saveAccountError,
    reset: resetSaveAccount,
  } = useSaveAccountMutation({ clientId });

  const onSubmit = formBag.handleSubmit(async (values: FormValues) => {
    try {
      const grantTypeOption = options.GrantTypeOptions.find(
        ({ value }) => value === values.grantType,
      );

      const newVestingList = (values.vestingList || []).filter(
        vest => vest.VestingDate && typeof vest.VestedShares === 'number',
      );

      const newGrant: Grant = {
        GrantType: values.grantType,
        Value: values.marketValue,
        TotalShares: values.totalShares,
        GrantDate: values.grantDate,
        Price: values.price,
        ExpectedReturn: values.expectedReturn,
        GrantTypeName: grantTypeOption?.label || '',
        VestingList: newVestingList,
        OwnedShares: values.ownedShares,
        OwnedExerciseDate: values.ownedExerciseDate,
        OwnedStrikePrice: values.ownedStrikePrice,
        OwnedFmvAtExercise: values.ownedFmvAtExercise,
        OwnedDispositionStrategy: values.ownedDispositionStrategy,
        OwnedDispositionDate: values.ownedDispositionDate,
        OwnedDispositionYear: values.ownedDispositionYear
          ? parseInt(values.ownedDispositionYear)
          : null,
      };

      let newGrantList: Grant[];

      if (isAddingNewGrant) {
        newGrantList = account.GrantList ? [...account.GrantList, newGrant] : [newGrant];
      } else {
        newGrantList = account.GrantList
          ? account.GrantList.map(g => {
              if (g.Id === grant.Id) {
                return {
                  ...g,
                  ...newGrant,
                };
              }
              return g;
            })
          : [newGrant];
      }

      const newAccount: Account = {
        ...account,
        AccountName: values.name,
        AccountOwner: values.owner,
        StockOptionAssetClass: values.assetClass,
        CustomAssetBucketIndex: values.customAssetBucketIndex,
        AccountEarmark: values.earmarks.map(({ value, label }) => ({
          Type: value,
          Tooltip: label,
        })),
        GrantList: newGrantList,
        OrionManagementStyleId: values.orionManagementStyleId,
        OwnerModel: values.ownerModel,
        StockOptionProductDetails: values.product
          ? {
              Id: values.product.id,
              Ticker: values.product.ticker,
              Price: values.product.price,
            }
          : null,
      };

      await saveAccount({ clientId, account: newAccount });

      toast.success(`Stock grant ${grant.Id ? 'saved' : 'added'} successfully.`);

      onClose();
    } catch (error) {
      console.error(error);
    }
  });

  return (
    <>
      <Modal
        isOpen
        onRequestClose={onClose}
        shouldCloseOnOverlayClick={false}
        isHidden={!!grantToDelete}
        aria={{ labelledby: 'upsert-grant--modal-header' }}
      >
        <ModalHeader as="h2" id="upsert-grant--modal-header">
          {`${grant.Id ? 'Edit' : 'Add'} Stock Grant`}
        </ModalHeader>
        <ModalCloseButton onClick={onClose} />

        {saveAccountError && (
          <Alert
            variant="error"
            message={saveAccountError.message}
            onRequestClose={resetSaveAccount}
            mb="16"
            mx="24"
          />
        )}

        <FormProvider {...formBag}>
          <form id="upsert_grant__form" onSubmit={onSubmit}>
            <Tabs>
              <TabList px="24">
                <Tab>Summary</Tab>
                <Tab>Details, Timing & Strategy</Tab>
              </TabList>

              <TabPanel>
                <ModalBody style={{ height: '60vh' }}>
                  <Summary isAddingNewGrant={isAddingNewGrant} clientId={clientId} />
                </ModalBody>
              </TabPanel>

              <TabPanel>
                <ModalBody style={{ height: '60vh', paddingLeft: 0, paddingRight: 0 }}>
                  <VestingSchedule />
                </ModalBody>
              </TabPanel>
            </Tabs>
          </form>
        </FormProvider>

        <ModalFooter>
          {grant.Id && (
            <Button
              variant="errorSecondary"
              onClick={() => setGrantToDelete({ grant, account } as any)}
              mr="auto"
            >
              Delete
            </Button>
          )}

          <Button variant="subtleInactive" onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            type="submit"
            form="upsert_grant__form"
            isLoading={isSavingAccount}
            ml="8"
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {grantToDelete && (
        <DeleteStockGrantModal
          {...grantToDelete}
          clientId={clientId}
          onClose={() => setGrantToDelete(null)}
          onSuccess={onClose}
        />
      )}
    </>
  );
};
